import type { DomainEvent, MainView, TutorialState } from '../../../core/domain/types';

export interface TutorialStepDefinition {
  id: string;
  title: string;
  message: string;
  view: MainView;
  targetId: string;
  expectedEvent: DomainEvent['type'];
}

export const TUTORIAL_STEPS: readonly TutorialStepDefinition[] = [
  {
    id: 'plant-crop',
    title: '播种',
    message: '已经为你选好萝卜种子，点击高亮的农田播种。',
    view: 'farm',
    targetId: 'farm-plot',
    expectedEvent: 'cropPlanted',
  },
  {
    id: 'harvest-crop',
    title: '收获',
    message: '萝卜成熟后，再点击高亮的农田完成收获。离开一会儿也会继续生长。',
    view: 'farm',
    targetId: 'farm-plot',
    expectedEvent: 'cropHarvested',
  },
  {
    id: 'sell-crop',
    title: '出售',
    message: '点击仓库中的出售按钮，把刚收获的萝卜换成金币。',
    view: 'farm',
    targetId: 'farm-sell-crop',
    expectedEvent: 'itemSold',
  },
  {
    id: 'decorate-home',
    title: '装修',
    message: '选择一件未摆放的家具，然后点击“保存装修”。',
    view: 'home',
    targetId: 'home-decorating',
    expectedEvent: 'furniturePlaced',
  },
  {
    id: 'change-outfit',
    title: '换装',
    message: '选择一件服装试穿，然后点击“确认穿搭”。',
    view: 'wardrobe',
    targetId: 'wardrobe-outfit',
    expectedEvent: 'clothingEquipped',
  },
];

export function normalizeTutorialState(tutorial: TutorialState): TutorialState {
  const step = Math.min(Math.max(0, Math.floor(tutorial.step)), TUTORIAL_STEPS.length);
  return { step, completed: tutorial.completed || step >= TUTORIAL_STEPS.length };
}

export function currentTutorialStep(tutorial: TutorialState): TutorialStepDefinition | null {
  const normalized = normalizeTutorialState(tutorial);
  return normalized.completed ? null : (TUTORIAL_STEPS[normalized.step] ?? null);
}

export function advanceTutorial(tutorial: TutorialState, event: DomainEvent): TutorialState {
  const normalized = normalizeTutorialState(tutorial);
  const step = currentTutorialStep(normalized);
  if (!step || step.expectedEvent !== event.type) return normalized;
  return skipTutorialStep(normalized);
}

export function skipTutorialStep(tutorial: TutorialState): TutorialState {
  const normalized = normalizeTutorialState(tutorial);
  if (normalized.completed) return normalized;
  const step = Math.min(normalized.step + 1, TUTORIAL_STEPS.length);
  return { step, completed: step >= TUTORIAL_STEPS.length };
}

export function completeTutorial(): TutorialState {
  return { step: TUTORIAL_STEPS.length, completed: true };
}
